import { DictType } from "@pma/types";
import { toast } from "@hooks/use-toast";
import {
  ADD_PROFILE_PICTURE_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { parseCookies } from "nookies";
import React, { useRef, useState } from "react";

import { Input } from "@components/common/input";
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar";
import { CheckCircleIcon, Edit } from "lucide-react";
import { Button } from "@components/common/button";
import { useAuth } from "@stores/auth-store";
import useTranslator from "@hooks/use-translator";

interface UserAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  user: DictType;
  refetch: CallableFunction;
}
const UserAvatar = ({ user, refetch }: UserAvatarProps) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<File>();

  const avatarInputRef = useRef<HTMLInputElement>(null);

  const { authConfig } = useAuth();

  const authUserId = authConfig?.id;

  const isLoggedInUser = authUserId === user?.id;

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { translate } = useTranslator();

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Read the selected file and convert it to a data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
      setAvatar(file);
    }
  };

  const uploadProfileAvatar = async (r: File) => {
    const formData = new FormData();
    formData.append("content", r, r.name);
    const response = await axios.post(
      SERVER_URL + ADD_PROFILE_PICTURE_ENDPOINT,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const updateProfileAvatarMutation = useMutation({
    mutationKey: ["update-profile-photo"],
    mutationFn: uploadProfileAvatar,
    onSuccess: (data: DictType) => {
      toast({
        title: translate("Success"),
        description: translate("Your profile photo has been updated"),
        variant: "success",
      });

      refetch();

      setUploadedImage(null);
      setAvatar(undefined);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: translate(
                errorMessage || `Unable to update your profile at this time`,
              ),
        variant: "error",
      });
    },
  });

  return (
    <div className="relative">
      {uploadedImage ? (
        <Avatar className="h-24 md:h-44 w-24 md:w-44">
          <AvatarImage src={uploadedImage} />
          <AvatarFallback className="font-bold">ROG</AvatarFallback>
        </Avatar>
      ) : (
        <Avatar className="h-24 md:h-44 w-24 md:w-44">
          <AvatarImage
            src={
              user?.user_picture?.url ||
              `${process.env.PUBLIC_URL}/user-profile.svg`
            }
          />
          <AvatarFallback className="font-bold">ROG</AvatarFallback>
        </Avatar>
      )}

      {isLoggedInUser && (
        <div className="absolute top-0 right-0">
          {uploadedImage ? (
            <Button
              size={"icon"}
              variant="secondary"
              title={translate("Save Profile Photo")}
              loading={updateProfileAvatarMutation.isPending}
              disabled={updateProfileAvatarMutation.isPending}
              className="rounded-full h-7 w-7"
            >
              <CheckCircleIcon
                onClick={() =>
                  avatar && updateProfileAvatarMutation.mutate(avatar)
                }
                size={16}
              />
            </Button>
          ) : (
            <Button
              size={"icon"}
              variant="default"
              title={translate("Edit Profile Photo")}
              className="rounded-full h-7 w-7"
            >
              <Edit onClick={() => avatarInputRef.current?.click()} size={16} />
            </Button>
          )}
          <label>
            <Input
              type="file"
              accept="image/png, image/gif, image/jpeg"
              capture="environment"
              className="hidden"
              onChange={handleFileInputChange}
              ref={avatarInputRef}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
